import type { LineItem, LineModifier, Modifier, ModifierGroup, Product } from "@shared/events";
import { MAX_MINOR } from "@shared/money";

/**
 * Pure cart model for the Sell screen (F-402, F-404, F-408, F-409). No React, no store: every rule the
 * register depends on is testable here, and the components stay dumb and fast.
 */

export const MAX_LINE_QTY = 999;

export interface CartLine {
  /** Identity of a line: same product + same modifier set stacks into one line. */
  key: string;
  productId: string;
  labelSnapshot: string;
  /** Base price plus modifier deltas, never negative. */
  unitPriceMinor: number;
  qty: number;
  modifiers: LineModifier[];
}

/** The customer picked for the sale being built (F-901–905): id for the event, display name for the bar. */
export interface CartCustomer {
  id: string;
  name: string;
}

/**
 * Cart shape persisted through the store (F-409). Versioned so a future change can be ignored safely.
 * `customer` was added in D-049 and `tipMinor` in D-063 (both optional, so an older v1 draft still reads
 * back fine): they are part of "the cart" and must survive a refresh exactly like the lines do.
 */
export interface CartDraft {
  v: 1;
  lines: CartLine[];
  customer?: CartCustomer | null;
  tipMinor?: number;
}

export function lineKey(productId: string, modifiers: readonly LineModifier[]): string {
  if (modifiers.length === 0) return productId;
  return `${productId}|${modifiers.map((m) => m.id).sort().join(",")}`;
}

export function toLineModifier(group: ModifierGroup, modifier: Modifier): LineModifier {
  return { id: modifier.id, groupId: group.id, label: modifier.label, priceDeltaMinor: modifier.priceDeltaMinor };
}

export function unitPriceMinor(product: Product, modifiers: readonly LineModifier[]): number {
  const delta = modifiers.reduce((sum, m) => sum + m.priceDeltaMinor, 0);
  return clampMinor(product.priceMinor + delta);
}

function clampMinor(value: number): number {
  return Math.max(0, Math.min(MAX_MINOR, Math.round(value)));
}

/** Add one unit. Stacks onto an existing line with the identical modifier set. */
export function addToCart(lines: readonly CartLine[], product: Product, modifiers: readonly LineModifier[] = []): CartLine[] {
  const mods = [...modifiers];
  const key = lineKey(product.id, mods);
  const existing = lines.find((l) => l.key === key);
  if (existing) return incrementLine(lines, key);
  return [
    ...lines,
    {
      key,
      productId: product.id,
      labelSnapshot: product.label,
      unitPriceMinor: unitPriceMinor(product, mods),
      qty: 1,
      modifiers: mods,
    },
  ];
}

export function incrementLine(lines: readonly CartLine[], key: string): CartLine[] {
  return lines.map((l) => (l.key === key ? { ...l, qty: Math.min(MAX_LINE_QTY, l.qty + 1) } : l));
}

/** Decrement by one; the line disappears when it would hit zero. */
export function decrementLine(lines: readonly CartLine[], key: string): CartLine[] {
  const out: CartLine[] = [];
  for (const l of lines) {
    if (l.key !== key) out.push(l);
    else if (l.qty > 1) out.push({ ...l, qty: l.qty - 1 });
  }
  return out;
}

export function removeLine(lines: readonly CartLine[], key: string): CartLine[] {
  return lines.filter((l) => l.key !== key);
}

export function lineTotalMinor(line: CartLine): number {
  return clampMinor(line.unitPriceMinor * line.qty);
}

export function cartTotalMinor(lines: readonly CartLine[]): number {
  return clampMinor(lines.reduce((sum, l) => sum + lineTotalMinor(l), 0));
}

export function cartCount(lines: readonly CartLine[]): number {
  return lines.reduce((sum, l) => sum + l.qty, 0);
}

export function toLineItems(lines: readonly CartLine[]): LineItem[] {
  return lines.map((l) => ({
    productId: l.productId,
    labelSnapshot: l.labelSnapshot,
    unitPriceMinor: l.unitPriceMinor,
    qty: l.qty,
    modifiers: l.modifiers,
    lineTotalMinor: lineTotalMinor(l),
  }));
}

/** Units of each product in the cart, for the "Sold out" warning against current stock (F-306). */
export function unitsByProduct(lines: readonly CartLine[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const l of lines) out[l.productId] = (out[l.productId] ?? 0) + l.qty;
  return out;
}

// ---------- Tip (D-063) ----------

/** The largest tip a cart can take: goods plus tip must still be a valid amount. */
export function maxTipMinor(goodsTotalMinor: number): number {
  return Math.max(0, MAX_MINOR - goodsTotalMinor);
}

/** A tip only exists alongside goods, and never pushes the amount due past `MAX_MINOR`. */
export function effectiveTipMinor(tipMinor: number, goodsTotalMinor: number, lineCount: number): number {
  if (lineCount === 0 || !Number.isInteger(tipMinor) || tipMinor <= 0) return 0;
  return Math.min(tipMinor, maxTipMinor(goodsTotalMinor));
}

// ---------- Cash math (F-404) ----------

/** Change due. Never negative: tendering less than the total is simply not enough. */
export function changeDueMinor(totalMinor: number, tenderedMinor: number): number {
  return Math.max(0, tenderedMinor - totalMinor);
}

export function isSufficientTender(totalMinor: number, tenderedMinor: number): boolean {
  return tenderedMinor >= totalMinor;
}

// ---------- Modifiers (F-304) ----------

export type Selection = Record<string, string[]>;

/** Pre-select nothing, except a single-choice required group, which defaults to its first option. */
export function initialSelection(groups: readonly ModifierGroup[]): Selection {
  const sel: Selection = {};
  for (const g of groups) {
    const first = [...g.modifiers].sort((a, b) => a.sortOrder - b.sortOrder)[0];
    sel[g.id] = g.minSelect >= 1 && g.maxSelect === 1 && first ? [first.id] : [];
  }
  return sel;
}

/**
 * Toggle one option. A max of 1 behaves like a radio (tapping another replaces it); above that, taps
 * past the max are ignored so the vendor never has to un-pick something to move on.
 */
export function toggleModifier(selection: Selection, group: ModifierGroup, modifierId: string): Selection {
  const current = selection[group.id] ?? [];
  let next: string[];
  if (current.includes(modifierId)) {
    next = current.filter((id) => id !== modifierId);
    if (next.length === 0 && group.minSelect >= 1 && group.maxSelect === 1) next = current;
  } else if (group.maxSelect === 1) {
    next = [modifierId];
  } else if (current.length < group.maxSelect) {
    next = [...current, modifierId];
  } else {
    next = current;
  }
  return { ...selection, [group.id]: next };
}

/** Human-readable reason the Add button is disabled, or null when the selection is valid. */
export function selectionError(groups: readonly ModifierGroup[], selection: Selection): string | null {
  for (const g of [...groups].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const n = (selection[g.id] ?? []).length;
    if (n < g.minSelect) {
      return g.minSelect === 1 ? `Choose ${g.name}` : `Choose at least ${g.minSelect} in ${g.name}`;
    }
    if (n > g.maxSelect) return `Choose at most ${g.maxSelect} in ${g.name}`;
  }
  return null;
}

export function isSelectionValid(groups: readonly ModifierGroup[], selection: Selection): boolean {
  return selectionError(groups, selection) === null;
}

/** Flatten a selection into line modifiers, in group then option order. */
export function selectionToModifiers(groups: readonly ModifierGroup[], selection: Selection): LineModifier[] {
  const out: LineModifier[] = [];
  for (const g of [...groups].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const ids = selection[g.id] ?? [];
    for (const m of [...g.modifiers].sort((a, b) => a.sortOrder - b.sortOrder)) {
      if (ids.includes(m.id)) out.push(toLineModifier(g, m));
    }
  }
  return out;
}

// ---------- Draft persistence (F-409) ----------

export function toDraft(lines: readonly CartLine[], customer: CartCustomer | null = null, tipMinor = 0): CartDraft {
  return { v: 1, lines: [...lines], customer, tipMinor };
}

/** Tolerant like draftToLines: anything but a positive whole number reads as "no tip". */
export function draftToTip(draft: unknown): number {
  if (!draft || typeof draft !== "object") return 0;
  const d = draft as Partial<CartDraft>;
  if (d.v !== 1) return 0;
  const tip = d.tipMinor;
  return typeof tip === "number" && Number.isInteger(tip) && tip > 0 ? Math.min(tip, MAX_MINOR) : 0;
}

/** Tolerant like draftToLines: anything but a well-formed pick reads as "no customer". */
export function draftToCustomer(draft: unknown): CartCustomer | null {
  if (!draft || typeof draft !== "object") return null;
  const d = draft as Partial<CartDraft>;
  if (d.v !== 1) return null;
  const c = d.customer;
  if (!c || typeof c !== "object" || typeof c.id !== "string" || typeof c.name !== "string") return null;
  return { id: c.id, name: c.name };
}

/** Tolerant: an unreadable or stale draft yields an empty cart rather than blocking the register. */
export function draftToLines(draft: unknown): CartLine[] {
  if (!draft || typeof draft !== "object") return [];
  const d = draft as Partial<CartDraft>;
  if (d.v !== 1 || !Array.isArray(d.lines)) return [];
  return d.lines.filter(
    (l): l is CartLine =>
      !!l &&
      typeof l.key === "string" &&
      typeof l.productId === "string" &&
      typeof l.labelSnapshot === "string" &&
      Number.isInteger(l.unitPriceMinor) &&
      Number.isInteger(l.qty) &&
      l.qty > 0 &&
      Array.isArray(l.modifiers),
  );
}

// ---------- Tile label auto-shrink (U-006) ----------

/**
 * Two display lines, shrunk by label length. Computed, not measured: a measuring pass would cost a
 * layout on every tile of a 60-product grid, and this screen must respond in under 50 ms (U-008).
 */
export function labelFontRem(label: string): number {
  const n = Array.from(label).length;
  if (n <= 8) return 1.125;
  if (n <= 12) return 1;
  if (n <= 17) return 0.875;
  if (n <= 22) return 0.8125;
  return 0.75;
}
